import { useEffect, useRef, useState, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, AreaChart, Area, BarChart, Bar, Cell, ReferenceDot, PieChart, Pie,
} from "recharts";
import { LineChart as TremorLineChart, type CustomTooltipProps as TremorTooltipProps } from "@tremor/react";
import { Calendar, ArrowUp, ArrowDown, ArrowUpRight, ChevronsUpDown, Eye, Info, ChevronLeft, ChevronRight, Triangle, Minus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchDashboardSummary, fetchProcessedInvoices, fetchBusinessDetails, fetchPriceChanges, fetchStorageIntegrations } from "../../services/apiService";
import { DashboardSummary, Invoice } from "../../types";
import type { BusinessDetails, StorageIntegration } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import { useBusiness } from "../../contexts/BusinessContext";
import { CATEGORY_COLORS, getCategoriesForIndustry } from "../../data/categories";
import { InvoiceDetailPanel } from "../../components/InvoiceDetailPanel";
import { InvoiceSlidingPanel } from "./InvoiceListPage";

// ── Date filter helpers ───────────────────────────────────────────────────────

type DateRange = "all" | "day" | "week" | "month" | "custom";
type CostChartMode = "monthly" | "weekly";

function getDateStart(range: DateRange): Date | null {
  if (range === "all" || range === "custom") return null;
  const now = new Date();
  if (range === "day") { const d = new Date(now); d.setHours(0, 0, 0, 0); return d; }
  if (range === "week") { const d = new Date(now); d.setDate(now.getDate() - 7); return d; }
  const d = new Date(now); d.setDate(now.getDate() - 30); return d;
}

function filterByDate(
  invoices: Invoice[],
  range: DateRange,
  customStart?: string,
  customEnd?: string,
): Invoice[] {
  if (range === "custom") {
    return invoices.filter((inv) => {
      const d = inv.invoiceDate;
      if (!d) return false;
      if (customStart && d < customStart) return false;
      if (customEnd && d > customEnd) return false;
      return true;
    });
  }
  const start = getDateStart(range);
  if (!start) return invoices;
  return invoices.filter((inv) => inv.invoiceDate && new Date(inv.invoiceDate) >= start);
}

// ── Design tokens — Bill Lens Hospitality Light ───────────────────────────────

const NAVY    = "#2a2420";   // ink
const ORANGE  = "#607840";   // accent — olive
const SURFACE = "#fff";      // card / panel background
const RAISED  = "#fff";      // panel header / table header
const LINE    = "#e9e1d4";   // border
const MUTED   = "#8a8074";   // secondary text
const FAINT   = "#aaa093";   // low-emphasis text
const CHIP    = "#f3ece1";   // chips, subtle backgrounds
const CANVAS  = "#fff";      // page background
const SHADOW  = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const UP      = "#c2412e";   // price up = bad
const UP_WEAK = "rgba(194,65,46,0.12)";
const DOWN    = "#3c7d57";   // price down = good
const DOWN_WEAK = "rgba(60,125,87,0.12)";

const DATE_FILTERS: { value: DateRange; label: string }[] = [
  { value: "all", label: "All" },
  { value: "day", label: "Today" },
  { value: "week", label: "7d" },
  { value: "month", label: "30d" },
  { value: "custom", label: "Custom" },
];

const EMPTY_SUMMARY: DashboardSummary = {
  totalInvoices: 0,
  totalSpend: 0,
  pendingProcessing: 0,
  flaggedCount: 0,
  avgProcessingTime: 0,
  spendByCategory: [],
  spendBySupplier: [],
  invoicesByStatus: [],
  monthlyTrend: [],
};

function getUserDisplayName(user?: { email?: string | null; user_metadata?: Record<string, unknown> } | null) {
  const metadata = user?.user_metadata ?? {};
  const rawName =
    (typeof metadata.name === "string" && metadata.name) ||
    (typeof metadata.full_name === "string" && metadata.full_name) ||
    (typeof metadata.display_name === "string" && metadata.display_name) ||
    "";
  const trimmedName = rawName.trim();
  if (trimmedName) return trimmedName;

  const localPart = user?.email?.split("@")[0]?.trim();
  if (!localPart) return "there";

  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const panelStyle = {
  backgroundColor: SURFACE,
  border: `1px solid ${LINE}`,
  boxShadow: SHADOW,
};

const panelHeaderStyle = {
  borderBottom: `1px solid ${LINE}`,
  backgroundColor: RAISED,
};

const graphPanelHeaderStyle = {
  backgroundColor: SURFACE,
};

function InfoTooltip({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  return (
    <span
      ref={ref}
      className="inline-flex"
      onMouseEnter={() => {
        if (ref.current) {
          const r = ref.current.getBoundingClientRect();
          setPos({ top: r.bottom + 6, left: r.left + r.width / 2 });
        }
      }}
      onMouseLeave={() => setPos(null)}
    >
      <span
        tabIndex={0}
        className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full"
        style={{ color: NAVY, opacity: 0.45, backgroundColor: "rgba(80,55,30,0.06)" }}
      >
        <Info size={9} />
      </span>
      {pos && createPortal(
        <span
          role="tooltip"
          className="pointer-events-none fixed z-[9999] w-56 -translate-x-1/2 rounded-md px-2.5 py-2 text-[11px] leading-relaxed shadow-lg"
          style={{ top: pos.top, left: pos.left, color: "white", backgroundColor: "rgba(80,55,30,0.95)" }}
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
}

function formatAxisCurrency(value: number) {
  if (Math.abs(value) < 1000) return `A$${value}`;
  return `A$${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
}

function numberValue(value: number | null | undefined) {
  return value ?? 0;
}

function displayCurrency(value: number | null | undefined) {
  return value === null || value === undefined
    ? "null"
    : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(value);
}

function displayText(value: string | number | null | undefined) {
  return value === null || value === undefined || value === "" ? "null" : String(value);
}

function PriceDirectionIcon({ current, previous }: { current: number | null | undefined; previous: number | null | undefined }) {
  if (current == null || previous == null || Math.abs(current - previous) < 0.0001) {
    return <Minus size={11} strokeWidth={2.2} style={{ color: MUTED }} />;
  }

  const isUp = current > previous;
  return (
    <Triangle
      size={10}
      fill="currentColor"
      strokeWidth={0}
      style={{
        color: isUp ? UP : DOWN,
        transform: isUp ? undefined : "rotate(180deg)",
      }}
    />
  );
}

function monthRange(month: string) {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return null;
  const start = `${year}-${String(monthNumber).padStart(2, "0")}-01`;
  const endDate = new Date(year, monthNumber, 0).getDate();
  const end = `${year}-${String(monthNumber).padStart(2, "0")}-${String(endDate).padStart(2, "0")}`;
  return { start, end };
}

function localIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMonth(yyyymm: string): string {
  const [year, month] = yyyymm.split("-");
  if (!year || !month) return yyyymm;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
}

function weekStartKey(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return localIsoDate(d);
}

function formatPeriod(key: string, granularity: "week" | "month"): string {
  if (granularity === "month") return formatMonth(key);
  const d = new Date(key + "T00:00:00");
  return d.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
}

function formatPeriodDetail(key: string, granularity: "week" | "month"): string {
  if (granularity === "month") return formatMonth(key);
  const start = parseIsoDate(key);
  if (!start) return key;
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const formatDay = (date: Date) => date.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  return `${formatDay(start)} - ${formatDay(end)}`;
}

function formatCompactDate(date: string | null | undefined): string {
  if (!date) return "—";
  const parsed = parseIsoDate(date.slice(0, 10));
  if (!parsed) return date;
  return parsed.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

function parseIsoDate(date: string) {
  if (!date) return null;
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

type StatTrendSet = { spend: StatTrendPoint[]; count: StatTrendPoint[] };
const EMPTY_STAT_TREND: StatTrendSet = { spend: [], count: [] };

function bucketInvoices(
  invoices: Invoice[],
  bucketKeys: string[],
  keyOf: (invoiceDate: string) => string,
  labelOf: (key: string) => string,
): StatTrendSet {
  const spendMap = new Map(bucketKeys.map((k) => [k, 0]));
  const countMap = new Map(bucketKeys.map((k) => [k, 0]));
  for (const inv of invoices) {
    if (!inv.invoiceDate) continue;
    const key = keyOf(inv.invoiceDate);
    if (!spendMap.has(key)) continue;
    spendMap.set(key, (spendMap.get(key) ?? 0) + numberValue(inv.totalAmount));
    countMap.set(key, (countMap.get(key) ?? 0) + 1);
  }
  return {
    spend: bucketKeys.map((k) => ({ key: k, label: labelOf(k), value: spendMap.get(k) ?? 0 })),
    count: bucketKeys.map((k) => ({ key: k, label: labelOf(k), value: countMap.get(k) ?? 0 })),
  };
}

function buildMonthlyTrend(invoices: Invoice[], months: number, anchorEnd: Date = new Date()): StatTrendSet {
  const keys = Array.from({ length: months }, (_, i) => {
    const d = addMonths(anchorEnd, -(months - 1 - i));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  return bucketInvoices(invoices, keys, (d) => d.slice(0, 7), (k) => formatMonth(k).slice(0, 3));
}

function buildWeeklyTrend(invoices: Invoice[], start: Date, end: Date): StatTrendSet {
  const keys: string[] = [];
  let cursor = weekStartKey(localIsoDate(start));
  const endKey = weekStartKey(localIsoDate(end));
  while (cursor <= endKey) {
    keys.push(cursor);
    const d = parseIsoDate(cursor)!;
    d.setDate(d.getDate() + 7);
    cursor = localIsoDate(d);
  }
  // Compact "day of week start" label — a full "23 Jun" doesn't fit the mini chart's tick spacing.
  const shortLabel = (k: string) => String(parseIsoDate(k)?.getDate() ?? k);
  return bucketInvoices(invoices, keys, (d) => weekStartKey(d), shortLabel);
}

function buildDailyTrend(invoices: Invoice[], start: Date, end: Date): StatTrendSet {
  const keys: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    keys.push(localIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  // Day-of-month number, not weekday name — an 8-day window (e.g. the "7d" filter) wraps past
  // a full week, so two buckets can land on the same weekday and look like duplicate/stuck labels.
  const shortLabel = (k: string) => String(parseIsoDate(k)?.getDate() ?? k);
  return bucketInvoices(invoices, keys, (d) => d.slice(0, 10), shortLabel);
}

// Buckets the same invoices already shown for the selected dashboard date filter, so the
// mini trend charts always sum to (and never contradict) the KPI number next to them.
function buildStatTrend(filteredInvoices: Invoice[], dateRange: DateRange, customStart: string, customEnd: string): StatTrendSet {
  if (dateRange === "day") return EMPTY_STAT_TREND; // single day has no meaningful sub-trend
  if (dateRange === "all") return buildMonthlyTrend(filteredInvoices, 6);

  let start: Date | null;
  let end = new Date();
  if (dateRange === "custom") {
    if (!customStart || !customEnd) return EMPTY_STAT_TREND;
    start = parseIsoDate(customStart);
    end = parseIsoDate(customEnd) ?? end;
  } else {
    start = getDateStart(dateRange);
  }
  if (!start) return EMPTY_STAT_TREND;

  const spanDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
  if (spanDays < 2) return EMPTY_STAT_TREND;
  if (spanDays <= 10) return buildDailyTrend(filteredInvoices, start, end);
  if (spanDays <= 70) return buildWeeklyTrend(filteredInvoices, start, end);
  // Count distinct calendar months touched by [start, end], not spanDays / 30 — an arbitrary
  // (non month-aligned) window can spill into one more calendar month than the day count implies.
  const monthsSpanned = (end.getFullYear() * 12 + end.getMonth()) - (start.getFullYear() * 12 + start.getMonth()) + 1;
  return buildMonthlyTrend(filteredInvoices, Math.min(12, monthsSpanned), end);
}

function monthLabel(date: Date) {
  return date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
}

function buildCalendarDays(monthDate: Date) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return {
      date: day,
      iso: localIsoDate(day),
      inMonth: day.getMonth() === month,
    };
  });
}

function buildCalendarWeeks(monthDate: Date) {
  const days = buildCalendarDays(monthDate);
  const weeks = Array.from({ length: 6 }, (_, index) => days.slice(index * 7, index * 7 + 7));
  return weeks.filter((week) => week.some((day) => day.inMonth));
}

function DateRangeCalendar({
  start,
  end,
  onChange,
}: {
  start: string;
  end: string;
  onChange: (next: { start: string; end: string }) => void;
}) {
  const initialMonth = parseIsoDate(start) ?? new Date();
  const [viewMonth, setViewMonth] = useState(() => new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
  const todayIso = localIsoDate(new Date());
  const [selectingEnd, setSelectingEnd] = useState(Boolean(start && !end));
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const previewEnd = selectingEnd ? hoverDate : null;
  const rangeEnd = end || previewEnd || "";

  function handleDateClick(iso: string) {
    if (!start || (start && end) || iso < start) {
      onChange({ start: iso, end: "" });
      setSelectingEnd(true);
      setHoverDate(null);
      return;
    }

    onChange({ start, end: iso });
    setSelectingEnd(false);
    setHoverDate(null);
  }

  function dayState(iso: string) {
    const isStart = iso === start;
    const isEnd = Boolean(end && iso === end);
    const isPreviewEnd = Boolean(!end && previewEnd && iso === previewEnd && iso !== start);
    const inRange = Boolean(start && rangeEnd && iso > start && iso < rangeEnd);
    const isToday = iso === todayIso;
    return { isStart, isEnd, isPreviewEnd, inRange, isToday };
  }

  function CalendarMonth({ month }: { month: Date }) {
    return (
      <div className="w-[260px] flex-shrink-0">
        <p className="text-center text-xs font-bold" style={{ color: NAVY }}>{monthLabel(month)}</p>
        <div className="mt-3 grid grid-cols-7 gap-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="h-7 text-center text-[10px] font-bold leading-7" style={{ color: NAVY, opacity: 0.35 }}>
              {day}
            </div>
          ))}
          {buildCalendarWeeks(month).flat().map(({ date, iso, inMonth }) => {
            if (!inMonth) {
              return <div key={iso} className="h-8" />;
            }

            const { isStart, isEnd, isPreviewEnd, inRange, isToday } = dayState(iso);
            const selected = isStart || isEnd || isPreviewEnd;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => handleDateClick(iso)}
                onMouseEnter={() => {
                  if (selectingEnd && start && iso >= start) setHoverDate(iso);
                }}
                className="h-8 rounded-lg text-xs font-semibold transition-colors"
                style={{
                  color: selected ? "white" : NAVY,
                  backgroundColor: selected
                    ? ORANGE
                    : inRange
                      ? "rgba(96,120,64,0.14)"
                      : "transparent",
                  border: isStart && selectingEnd && !end
                    ? `1px solid ${ORANGE}`
                    : isToday && !selected
                      ? `1px solid ${ORANGE}`
                      : "1px solid transparent",
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div onMouseLeave={() => setHoverDate(null)}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setViewMonth((month) => addMonths(month, -1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{ color: NAVY, backgroundColor: `${NAVY}08` }}
          title="Previous month"
        >
          <ChevronLeft size={15} />
        </button>
        <div />
        <button
          type="button"
          onClick={() => setViewMonth((month) => addMonths(month, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{ color: NAVY, backgroundColor: `${NAVY}08` }}
          title="Next month"
        >
          <ChevronRight size={15} />
        </button>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <CalendarMonth month={viewMonth} />
        <CalendarMonth month={addMonths(viewMonth, 1)} />
      </div>
    </div>
  );
}

function buildWeeklyData(invoices: Invoice[], numWeeks = 8) {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - daysToMonday);
  thisMonday.setHours(0, 0, 0, 0);

  return Array.from({ length: numWeeks }, (_, i) => {
    const weekStart = new Date(thisMonday);
    weekStart.setDate(thisMonday.getDate() - (numWeeks - 1 - i) * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const start = localIsoDate(weekStart);
    const end = localIsoDate(weekEnd);
    const weekInvoices = invoices.filter((inv) => {
      const d = inv.invoiceDate;
      return Boolean(d && d >= start && d <= end);
    });
    return {
      label: weekStart.toLocaleDateString("en-AU", { month: "short", day: "numeric" }),
      start,
      end,
      amount: weekInvoices.reduce((sum, inv) => sum + numberValue(inv.totalAmount), 0),
      count: weekInvoices.length,
    };
  });
}

// ── Price Change helpers ──────────────────────────────────────────────────────

type PriceChangeRow = {
  key: string;
  productCode: string;
  description: string;
  supplierName: string;
  supplierCode: string;
  currentInvoiceId: string;
  prevInvoiceId: string | null;
  prevPrice: number | null;
  currentPrice: number;
  prevDate: string | null;
  currentDate: string;
  direction: "up" | "down" | "same" | "new";
};

function invScanDate(inv: Invoice): string | null {
  if (inv.uploadedAt) return inv.uploadedAt.slice(0, 10);
  return inv.invoiceDate;
}

function buildPriceChangeRows(invoices: Invoice[], nowDate: string): PriceChangeRow[] {
  if (!nowDate) return [];

  type NowAcc = { priceSum: number; count: number; description: string; supplierName: string; supplierCode: string; invoiceId: string };
  type HistoryEntry = { scanDate: string; unitPrice: number; invoiceId: string };

  const nowMap = new Map<string, NowAcc>();
  const historyMap = new Map<string, HistoryEntry[]>();

  for (const inv of invoices) {
    const scanDate = invScanDate(inv);
    if (!scanDate) continue;
    for (const item of inv.lineItems) {
      if (!item.productCode || item.unitPrice == null) continue;
      const key = `${inv.supplier.id}||${item.productCode}`;

      if (scanDate === nowDate) {
        if (!nowMap.has(key)) {
          nowMap.set(key, { priceSum: 0, count: 0, description: item.description || item.productCode, supplierName: inv.supplier.name, supplierCode: inv.supplier.code, invoiceId: inv.id });
        }
        const e = nowMap.get(key)!;
        e.priceSum += item.unitPrice;
        e.count += 1;
        e.invoiceId = inv.id;
      } else if (scanDate < nowDate) {
        if (!historyMap.has(key)) historyMap.set(key, []);
        historyMap.get(key)!.push({ scanDate, unitPrice: item.unitPrice, invoiceId: inv.id });
      }
    }
  }

  const rows: PriceChangeRow[] = [];
  for (const [key, nowEntry] of nowMap.entries()) {
    const nowPrice = nowEntry.priceSum / nowEntry.count;
    const history = (historyMap.get(key) ?? []).sort((a, b) => b.scanDate.localeCompare(a.scanDate));
    const prevEntry = history.find((e) => Math.abs(e.unitPrice - nowPrice) >= 0.0001);
    if (!prevEntry) continue;

    const productCode = key.split("||").slice(1).join("||");
    rows.push({
      key,
      productCode,
      description: nowEntry.description,
      supplierName: nowEntry.supplierName,
      supplierCode: nowEntry.supplierCode,
      currentInvoiceId: nowEntry.invoiceId,
      prevInvoiceId: prevEntry.invoiceId,
      prevPrice: prevEntry.unitPrice,
      currentPrice: nowPrice,
      prevDate: prevEntry.scanDate,
      currentDate: nowDate,
      direction: nowPrice > prevEntry.unitPrice ? "up" : "down",
    });
  }

  return rows.sort((a, b) => {
    const pctA = a.prevPrice! !== 0 ? (a.currentPrice - a.prevPrice!) / a.prevPrice! : 0;
    const pctB = b.prevPrice! !== 0 ? (b.currentPrice - b.prevPrice!) / b.prevPrice! : 0;
    return pctB - pctA;
  });
}

// ── Price Index helpers ───────────────────────────────────────────────────────

const PRICE_LINE_COLORS = [
  "#EF4444", "#F59E0B", "#10B981", "#06B6D4", "#8B5CF6",
  "#EC4899", "#F97316", "#0EA5E9", "#84CC16", "#6366F1",
];

function stableColor(key: string): string {
  let h = 5381;
  for (let i = 0; i < key.length; i++) h = ((h << 5) + h) ^ key.charCodeAt(i);
  return PRICE_LINE_COLORS[Math.abs(h) % PRICE_LINE_COLORS.length];
}

type ItemPriceSeries = {
  key: string;
  productCode: string;
  description: string;
  supplierName: string;
  supplierId: string;
  category: string;
  unit: string;
  color: string;
  totalSpend: number;
  baselinePrice: number;
  baselineQty: number;
  baselineMonth: string;
  monthPrices: Map<string, number>;
};

type PriceIndexData = {
  chartData: Array<Record<string, number | string>>;
  series: ItemPriceSeries[];
  indexPctChange: number;
  indexDollarChange: number;
  latestIndex: number;
  latestTotalSpend: number;
  baselinePeriod: string | null;
  suppliers: Array<{ id: string; name: string }>;
};

function buildPriceIndexData(invoices: Invoice[], rootDate?: string, granularity: "week" | "month" = "week"): PriceIndexData {
  const rootDay = rootDate?.slice(0, 10) || null;
  const rootPeriod = rootDay
    ? (granularity === "week" ? weekStartKey(rootDay) : rootDay.slice(0, 7))
    : null;
  const rawMap = new Map<string, {
    productCode: string;
    description: string;
    supplierName: string;
    supplierId: string;
    category: string;
    unit: string;
    totalSpend: number;
    months: Map<string, { priceSum: number; count: number; qtySum: number }>;
  }>();

  const supplierMap = new Map<string, { id: string; name: string }>();
  const periodSpendMap = new Map<string, number>();

  for (const inv of invoices) {
    if (inv.status === "duplicate") continue;
    if (!inv.invoiceDate) continue;
    const invoiceDay = inv.invoiceDate.slice(0, 10);
    if (rootDay && invoiceDay < rootDay) continue;
    const period = granularity === "week" ? weekStartKey(invoiceDay) : invoiceDay.slice(0, 7);
    supplierMap.set(inv.supplier.id, { id: inv.supplier.id, name: inv.supplier.name });
    const invoiceSpend = inv.totalAmount ?? inv.lineItems.reduce((sum, item) => sum + numberValue(item.totalPrice), 0);
    periodSpendMap.set(period, (periodSpendMap.get(period) ?? 0) + numberValue(invoiceSpend));

    for (const item of inv.lineItems) {
      if (!item.productCode || item.unitPrice == null) continue;
      const unitKey = (item.unit ?? "").trim().toLowerCase();
      const key = `${inv.supplier.id}||${item.productCode}||${unitKey}`;

      if (!rawMap.has(key)) {
        rawMap.set(key, {
          productCode: item.productCode,
          description: item.description || item.productCode,
          supplierName: inv.supplier.name,
          supplierId: inv.supplier.id,
          category: item.category,
          unit: item.unit ?? "",
          totalSpend: 0,
          months: new Map(),
        });
      }

      const entry = rawMap.get(key)!;
      entry.totalSpend += item.totalPrice ?? 0;

      if (!entry.months.has(period)) entry.months.set(period, { priceSum: 0, count: 0, qtySum: 0 });
      const m = entry.months.get(period)!;
      const qty = item.quantity ?? 1;
      m.priceSum += item.unitPrice * qty;
      m.count += 1;
      m.qtySum += qty;
    }
  }

  if (rawMap.size === 0) {
    return { chartData: [], series: [], indexPctChange: 0, indexDollarChange: 0, latestIndex: 100, latestTotalSpend: 0, baselinePeriod: null, suppliers: [] };
  }

  const seriesList: ItemPriceSeries[] = [];

  for (const [key, raw] of rawMap.entries()) {
    const monthPrices = new Map<string, number>();
    const monthQtys = new Map<string, number>();

    for (const [period, { priceSum, count, qtySum }] of raw.months.entries()) {
      monthPrices.set(period, qtySum > 0 ? priceSum / qtySum : priceSum / count);
      monthQtys.set(period, qtySum / count);
    }

    const sortedPeriods = [...monthPrices.keys()].sort();
    if (sortedPeriods.length < 2) continue;

    // Baseline = first invoice at or after rootDate; fall back to last available if rootDate is beyond all data
    let baselineMonth: string;
    if (rootPeriod) {
      const atOrAfter = sortedPeriods.filter((p) => p >= rootPeriod);
      baselineMonth = atOrAfter.length > 0 ? atOrAfter[0] : sortedPeriods[sortedPeriods.length - 1];
    } else {
      baselineMonth = sortedPeriods[0];
    }

    seriesList.push({
      key,
      productCode: raw.productCode,
      description: raw.description,
      supplierName: raw.supplierName,
      supplierId: raw.supplierId,
      category: raw.category,
      unit: raw.unit,
      color: stableColor(key),
      totalSpend: raw.totalSpend,
      baselinePrice: monthPrices.get(baselineMonth)!,
      baselineQty: monthQtys.get(baselineMonth)!,
      baselineMonth,
      monthPrices,
    });
  }

  if (seriesList.length === 0) {
    return { chartData: [], series: [], indexPctChange: 0, indexDollarChange: 0, latestIndex: 100, latestTotalSpend: 0, baselinePeriod: null, suppliers: [...supplierMap.values()].sort((a, b) => a.name.localeCompare(b.name)) };
  }

  seriesList.sort((a, b) => b.totalSpend - a.totalSpend);

  const sortedAllPeriods = [
    ...new Set([
      ...seriesList.flatMap((series) => [...series.monthPrices.keys()]),
      ...periodSpendMap.keys(),
    ]),
  ].sort();
  const baselinePeriod = rootPeriod
    ? (sortedAllPeriods.find((period) => period >= rootPeriod) ?? sortedAllPeriods[sortedAllPeriods.length - 1] ?? null)
    : (sortedAllPeriods[0] ?? null);
  const totalSpendAll = seriesList.reduce((s, i) => s + i.totalSpend, 0);
  const lastKnownPrice = new Map<string, number>();

  const chartData = sortedAllPeriods.map((period) => {
    const point: Record<string, number | string> = { month: period };
    let weightedRatioSum = 0;
    let weightSum = 0;
    let periodDollar = 0;

    for (const s of seriesList) {
      if (s.baselinePrice <= 0) continue;
      if (period < s.baselineMonth) continue;
      const price = s.monthPrices.get(period);
      if (price !== undefined) {
        point[s.key] = ((price / s.baselinePrice) - 1) * 100;
        lastKnownPrice.set(s.key, price);
      }
      const effectivePrice = lastKnownPrice.get(s.key) ?? s.baselinePrice;
      if (totalSpendAll > 0) {
        const weight = s.totalSpend / totalSpendAll;
        weightedRatioSum += (effectivePrice / s.baselinePrice) * weight;
        weightSum += weight;
      }
      periodDollar += (effectivePrice - s.baselinePrice) * s.baselineQty;
    }

    point.index = weightSum > 0 ? ((weightedRatioSum / weightSum) - 1) * 100 : 0;
    point.basketDelta = periodDollar;
    point.totalSpend = periodSpendMap.get(period) ?? 0;
    return point;
  });

  const latestIndexPct = chartData.length > 0 ? Number(chartData[chartData.length - 1].index) : 0;
  const latestIndex = latestIndexPct + 100;
  const indexPctChange = latestIndexPct;
  const latestTotalSpend = chartData.length > 0 ? Number(chartData[chartData.length - 1].totalSpend) || 0 : 0;

  let indexDollarChange = 0;
  for (const s of seriesList) {
    const sortedItemPeriods = [...s.monthPrices.keys()].sort();
    if (sortedItemPeriods.length < 2) continue;
    const latestPrice = s.monthPrices.get(sortedItemPeriods[sortedItemPeriods.length - 1])!;
    indexDollarChange += (latestPrice - s.baselinePrice) * s.baselineQty;
  }

  return {
    chartData,
    series: seriesList,
    indexPctChange,
    indexDollarChange,
    latestIndex,
    latestTotalSpend,
    baselinePeriod,
    suppliers: [...supplierMap.values()].sort((a, b) => a.name.localeCompare(b.name)),
  };
}

// ── StatCard ──────────────────────────────────────────────────────────────────

type StatTrendPoint = { key: string; label: string; value: number };

type StatCardProps = {
  label: string;
  value: string;
  info?: string;
  change?: { pct: number; compareLabel: string; invert?: boolean } | null;
  sub?: string;
  trend?: StatTrendPoint[];
  chartType?: "area" | "bar";
  chartValueFormatter?: (value: number) => string;
  onClick?: () => void;
};

function StatCard({ label, value, info, change, sub, trend, chartType = "bar", chartValueFormatter, onClick }: StatCardProps) {
  const isGood = change != null ? (change.invert ? change.pct <= 0 : change.pct >= 0) : null;
  const changeColor = isGood == null ? MUTED : isGood ? DOWN : UP;
  const gradientId = `statGrad-${label.replace(/[^a-z0-9]/gi, "")}`;
  const points = trend ?? [];
  const hasTrend = points.length > 1;

  let peak: StatTrendPoint | null = null;
  if (chartType === "area" && points.length > 0) {
    peak = points.reduce((max, p) => (p.value > max.value ? p : max), points[0]);
  }

  return (
    <div
      className={`rounded-2xl px-5 py-4 flex items-center justify-between gap-4 transition-shadow duration-150 ${onClick ? "cursor-pointer" : "cursor-default"}`}
      style={{ ...panelStyle }}
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 14px rgba(80,55,30,.10)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = SHADOW)}
    >
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <p className="text-[12.5px] font-semibold truncate" style={{ color: NAVY }}>{label}</p>
          {info && <InfoTooltip text={info} />}
          {onClick && <ArrowUpRight size={12} style={{ color: MUTED, flexShrink: 0 }} />}
        </div>
        <p className="text-[26px] font-bold leading-none tracking-tight"
          style={{ color: NAVY, fontVariantNumeric: "tabular-nums" }}>{value}</p>
        {change != null ? (
          <div className="inline-flex items-center gap-1 whitespace-nowrap">
            <Triangle
              size={9}
              fill="currentColor"
              strokeWidth={0}
              style={{ color: changeColor, transform: isGood ? undefined : "rotate(180deg)", flexShrink: 0 }}
            />
            <span className="text-[11.5px] font-bold" style={{ color: changeColor }}>
              {Math.abs(change.pct).toFixed(1)}%
            </span>
            <span className="text-[11.5px]" style={{ color: MUTED }}>{change.compareLabel}</span>
          </div>
        ) : sub ? (
          <p className="text-[11.5px]" style={{ color: MUTED }}>{sub}</p>
        ) : null}
      </div>

      {hasTrend && (
        <div className="flex-shrink-0" style={{ width: 108, height: chartType === "bar" ? 64 : 56 }}>
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "area" ? (
              <AreaChart data={points} margin={{ top: 16, right: 8, left: 4, bottom: 0 }}>
                <XAxis dataKey="key" hide />
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={DOWN} stopOpacity={0.32} />
                    <stop offset="100%" stopColor={DOWN} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={DOWN}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  isAnimationActive={false}
                />
                {peak && (
                  <ReferenceDot
                    x={peak.key}
                    y={peak.value}
                    r={3}
                    fill={DOWN}
                    stroke={SURFACE}
                    strokeWidth={2}
                    label={(props: { viewBox?: { x?: number; y?: number } }) => {
                      const isLast = peak!.key === points[points.length - 1].key;
                      const x = props.viewBox?.x ?? 0;
                      const y = props.viewBox?.y ?? 0;
                      return (
                        <text
                          x={x + (isLast ? -5 : 0)}
                          y={y - 7}
                          textAnchor={isLast ? "end" : "middle"}
                          fontSize={10}
                          fontWeight={700}
                          fill={DOWN}
                        >
                          {chartValueFormatter ? chartValueFormatter(peak!.value) : String(peak!.value)}
                        </text>
                      );
                    }}
                  />
                )}
              </AreaChart>
            ) : (
              <BarChart data={points} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barCategoryGap="28%">
                <Bar dataKey="value" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  {points.map((point, i) => (
                    <Cell key={point.key} fill={i === points.length - 1 ? DOWN : DOWN_WEAK} />
                  ))}
                </Bar>
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  tick={{ fontSize: 9, fill: MUTED }}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ── Supplier table sort ───────────────────────────────────────────────────────

type SupplierSortCol = "name" | "category" | "invoiceCount" | "totalSpend" | "lastInvoiceDate" | "avgInvoice";
type SortDir = "asc" | "desc";
type SupplierSort = { col: SupplierSortCol | null; dir: SortDir };

function SortableHeader({
  label, col, sort, onSort, align = "left",
}: {
  label: string;
  col: SupplierSortCol;
  sort: SupplierSort;
  onSort: (col: SupplierSortCol) => void;
  align?: "left" | "center" | "right";
}) {
  const active = sort.col === col;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  const justifyClass = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  return (
    <th className={`px-5 py-3 ${alignClass}`} style={{ color: NAVY }}>
      <button
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 ${justifyClass} group`}
      >
        <span className="text-xs font-semibold uppercase tracking-wide transition-opacity"
          style={{ opacity: active ? 0.8 : 0.5 }}>
          {label}
        </span>
        <Icon
          size={12}
          style={{ color: active ? ORANGE : NAVY, opacity: active ? 1 : 0.3 }}
          className="transition-all group-hover:opacity-70"
        />
      </button>
    </th>
  );
}

function CategoryPill({ category }: { category: string }) {
  return (
    <span
      className="text-xs font-semibold"
      style={{ color: NAVY, opacity: 0.64 }}
    >
      {category}
    </span>
  );
}

// ── PriceChangePanel ──────────────────────────────────────────────────────────

type PriceSortKey = "productCode" | "supplierName" | "description" | "prevDate" | "prevPrice" | "currentPrice" | "changePct";

function PriceChangePanel({
  invoices, businessId, categories,
}: {
  invoices: Invoice[];
  businessId?: string;
  categories: string[];
}) {
  type Selection = { invoiceId: string; prevInvoiceId: string | null; productCode: string };
  const [selected, setSelected] = useState<Selection | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<PriceSortKey>("changePct");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const scanDates = useMemo(
    () => [...new Set(invoices.map(invScanDate).filter(Boolean) as string[])].sort().reverse(),
    [invoices],
  );

  const [nowScan, setNowScan] = useState("");
  const effectiveNow = nowScan || scanDates[0] || "";

  const [rows, setRows] = useState<PriceChangeRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const { data: storageIntegrationsData } = useQuery<StorageIntegration[]>({
    queryKey: ["storage-integrations", businessId],
    queryFn: () => fetchStorageIntegrations(businessId),
    enabled: Boolean(businessId),
  });
  const storageIntegrations: StorageIntegration[] = storageIntegrationsData ?? [];
  const deletesExternalOnInvoiceDelete = storageIntegrations.some((integration) =>
    integration.status === "connected" && integration.deleteOnInvoiceDelete
  );

  useEffect(() => {
    if (!effectiveNow) { setRows([]); return; }
    let cancelled = false;
    setRowsLoading(true);
    fetchPriceChanges(effectiveNow, businessId).then((data) => {
      if (cancelled) return;
      setRows(data.map((c: any) => ({
        key: c.id,
        productCode: c.productCode || "",
        description: c.itemName,
        supplierName: c.supplier?.name || "",
        supplierCode: c.supplier?.code || "",
        currentInvoiceId: c.newInvoiceId,
        prevInvoiceId: c.oldInvoiceId || null,
        prevPrice: c.oldPrice ?? null,
        currentPrice: c.newPrice,
        prevDate: c.prevDate || null,
        currentDate: c.discoveredAt,
        direction: c.newPrice > c.oldPrice ? "up" : "down",
      })));
    }).catch(() => {
      if (!cancelled) setRows([]);
    }).finally(() => {
      if (!cancelled) setRowsLoading(false);
    });
    return () => { cancelled = true; };
  }, [effectiveNow, businessId]);

  function handleSort(key: PriceSortKey) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  }

  function SortTh({ col, label, align = "left" }: { col: PriceSortKey; label: string; align?: "left" | "right" }) {
    const active = sortKey === col;
    return (
      <th
        className={`px-4 py-2.5 text-${align} text-sm font-bold cursor-pointer select-none`}
        style={{ color: NAVY, opacity: active ? 0.9 : 0.5 }}
        onClick={() => handleSort(col)}
      >
        <span className="inline-flex items-center gap-0.5">
          {align === "right" && <ChevronsUpDown size={10} style={{ opacity: active ? 1 : 0.4 }} />}
          {label}
          {align === "left" && <ChevronsUpDown size={10} style={{ opacity: active ? 1 : 0.4 }} />}
        </span>
      </th>
    );
  }

  const sortedRows = useMemo(() => {
    const getValue = (row: PriceChangeRow): string | number => {
      const pct = row.prevPrice && row.prevPrice !== 0 ? (row.currentPrice - row.prevPrice) / row.prevPrice : 0;
      switch (sortKey) {
        case "productCode":   return row.productCode.toLowerCase();
        case "supplierName":  return row.supplierName.toLowerCase();
        case "description":   return row.description.toLowerCase();
        case "prevDate":      return row.prevDate ?? "";
        case "prevPrice":     return row.prevPrice ?? -Infinity;
        case "currentPrice":  return row.currentPrice;
        case "changePct":     return pct;
      }
    };
    return [...rows].sort((a, b) => {
      const av = getValue(a), bv = getValue(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);
  const close = () => setSelected(null);
  const currentInvoice = selected ? invoices.find((inv) => inv.id === selected.invoiceId) ?? null : null;
  const prevInvoice = selected?.prevInvoiceId ? invoices.find((inv) => inv.id === selected.prevInvoiceId) ?? null : null;
  const hasBoth = Boolean(currentInvoice && prevInvoice);

  return (
    <>
      {/* Single invoice view */}
      {currentInvoice && !hasBoth && (
        <InvoiceSlidingPanel
          inv={currentInvoice}
          businessId={businessId}
          categories={categories}
          deletesExternalOnInvoiceDelete={deletesExternalOnInvoiceDelete}
          onClose={close}
        />
      )}

      {/* Invoice comparison modal */}
      {currentInvoice && hasBoth && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(80,55,30,0.55)" }}
          onClick={close}
        >
          <div
            className={`w-full rounded-xl bg-white overflow-hidden flex flex-col ${hasBoth ? "max-w-[94vw]" : "max-w-5xl"}`}
            style={{ boxShadow: "0 24px 80px rgba(80,55,30,0.28)", maxHeight: "92vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top bar */}
            <div className="px-6 py-3 flex items-center justify-between flex-shrink-0" style={{ borderBottom: `1px solid ${NAVY}14`, backgroundColor: "rgba(134,187,216,0.06)" }}>
              <p className="text-sm font-bold" style={{ color: NAVY }}>
                {hasBoth ? `Price Comparison · ${selected!.productCode}` : `Invoice ${currentInvoice.invoiceNumber ?? "—"}`}
              </p>
              <button onClick={close} className="rounded-md px-3 py-1.5 text-xs font-bold" style={{ backgroundColor: RAISED, color: NAVY }}>
                Close
              </button>
            </div>

            <div className="flex flex-1 min-h-0" style={{ borderTop: `1px solid ${LINE}` }}>
                {/* Left: previous invoice */}
                <div className="flex-1 flex flex-col min-w-0" style={{ borderRight: `1px solid ${NAVY}12` }}>
                  <div className="px-5 py-2.5 flex-shrink-0" style={{ borderBottom: `1px solid ${NAVY}0a`, backgroundColor: `${NAVY}04` }}>
                    <p className="text-[9px] font-bold uppercase tracking-widest mb-0.5" style={{ color: NAVY, opacity: 0.4 }}>Previous</p>
                    <p className="text-xs font-semibold" style={{ color: NAVY }}>
                      {prevInvoice!.invoiceNumber ?? "—"} · {prevInvoice!.supplier.name} · {prevInvoice!.invoiceDate ?? "—"}
                    </p>
                  </div>
                  <div className="overflow-y-auto flex-1 p-5">
                    <InvoiceDetailPanel
                      inv={prevInvoice!}
                      businessId={businessId}
                      categories={categories}
                      onInvoiceDeleted={close}
                      highlightProductCode={selected?.productCode}
                      lineItemsOnly
                    />
                  </div>
                </div>
                {/* Right: current invoice */}
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="px-5 py-2.5 flex-shrink-0" style={{ borderBottom: `1px solid ${NAVY}0a`, backgroundColor: "rgba(96,120,64,0.05)" }}>
                    <p className="text-[9px] font-bold uppercase tracking-widest mb-0.5" style={{ color: ORANGE, opacity: 0.8 }}>Current</p>
                    <p className="text-xs font-semibold" style={{ color: NAVY }}>
                      {currentInvoice.invoiceNumber ?? "—"} · {currentInvoice.supplier.name} · {currentInvoice.invoiceDate ?? "—"}
                    </p>
                  </div>
                  <div className="overflow-y-auto flex-1 p-5">
                    <InvoiceDetailPanel
                      inv={currentInvoice}
                      businessId={businessId}
                      categories={categories}
                      onInvoiceDeleted={close}
                      highlightProductCode={selected?.productCode}
                      lineItemsOnly
                    />
                  </div>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel */}
      <div className="rounded-xl overflow-hidden flex flex-col" style={{ ...panelStyle, height: 540 }}>
        <div
          className="px-5 py-4 flex-shrink-0"
          style={panelHeaderStyle}
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <h2 className="text-sm font-bold" style={{ color: NAVY }}>Price Change Detector</h2>
              <InfoTooltip text="For each item in the selected scan, finds the most recent prior scan where the price was different. Prev Date shows when that item last changed." />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {effectiveNow && (
                <select
                  value={effectiveNow}
                  onChange={(e) => setNowScan(e.target.value)}
                  className="text-xs font-medium rounded-full px-3 py-1 cursor-pointer"
                  style={{ color: NAVY, backgroundColor: CHIP, border: `1px solid ${LINE}`, outline: "none" }}
                >
                  {scanDates.map((d) => {
                    const [y, m, dd] = d.split("-").map(Number);
                    const label = new Date(y, m - 1, dd).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
                    return <option key={d} value={d}>Scan {label}</option>;
                  })}
                </select>
              )}
              {rows.filter((r) => r.direction === "up").length > 0 && (
                <span className="rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ backgroundColor: UP_WEAK, color: UP }}>
                  {rows.filter((r) => r.direction === "up").length} ▲
                </span>
              )}
              {rows.filter((r) => r.direction === "down").length > 0 && (
                <span className="rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ backgroundColor: DOWN_WEAK, color: DOWN }}>
                  {rows.filter((r) => r.direction === "down").length} ▼
                </span>
              )}
            </div>
          </div>
        </div>
        {rowsLoading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <p className="text-sm" style={{ color: NAVY, opacity: 0.4 }}>Loading…</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <p className="text-sm" style={{ color: NAVY, opacity: 0.4 }}>No price changes found.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "4%" }} />
              </colgroup>
              <thead
                className="sticky top-0"
                style={{ backgroundColor: "#fff", borderBottom: `1px solid ${NAVY}14` }}
              >
                <tr>
                  <SortTh col="productCode"  label="Code" />
                  <SortTh col="supplierName" label="Supplier" />
                  <SortTh col="description"  label="Description" />
                  <SortTh col="prevDate"     label="Prev date" />
                  <SortTh col="prevPrice"    label="Prev"   align="right" />
                  <SortTh col="currentPrice" label="Now"    align="right" />
                  <SortTh col="changePct"    label="Change" align="right" />
                  <th className="py-2.5" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, i) => {
                  const pct = row.prevPrice && row.prevPrice !== 0
                    ? ((row.currentPrice - row.prevPrice) / row.prevPrice) * 100
                    : null;
                  const pctStr = pct !== null ? `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%` : null;
                  const isExpanded = expandedKey === row.key;
                  const supplierId = invoices.find((inv) => inv.id === row.currentInvoiceId)?.supplier.id;
                  const descKey = row.description.toLowerCase().trim();
                  const invoiceHistory = isExpanded
                    ? invoices
                        .filter((inv) => inv.supplier.id === supplierId)
                        .flatMap((inv) =>
                          inv.lineItems
                            .filter((item) =>
                              row.productCode
                                ? item.productCode === row.productCode
                                : (item.description || "").toLowerCase().trim() === descKey,
                            )
                            .map((item) => ({
                              invoiceId: inv.id,
                              invoiceNumber: inv.invoiceNumber,
                              invoiceDate: inv.invoiceDate,
                              quantity: item.quantity,
                              unit: item.unit,
                              unitPrice: item.unitPrice,
                              totalPrice: item.totalPrice,
                            })),
                        )
                        .sort((a, b) => (b.invoiceDate ?? "").localeCompare(a.invoiceDate ?? ""))
                    : [];
                  return (
                    <Fragment key={row.key}>
                    <tr
                      onClick={() => setExpandedKey(isExpanded ? null : row.key)}
                      style={{ borderBottom: `1px solid ${NAVY}0a`, backgroundColor: isExpanded ? "rgba(96,120,64,0.08)" : SURFACE, cursor: "pointer" }}
                      onMouseEnter={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = "rgba(96,120,64,0.06)")}
                      onMouseLeave={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = SURFACE)}
                    >
                      <td className="px-4 py-3 overflow-hidden">
                        <span className="font-mono text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ backgroundColor: CHIP, color: NAVY, border: `1px solid ${LINE}` }}>
                          {row.productCode}
                        </span>
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <p className="text-xs font-semibold truncate" style={{ color: NAVY }}>{row.supplierName}</p>
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <p className="text-xs font-medium truncate" style={{ color: NAVY }}>{row.description}</p>
                      </td>
                      <td className="px-4 py-3 text-xs overflow-hidden" style={{ color: MUTED }}>
                        {formatCompactDate(row.prevDate)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs overflow-hidden" style={{ color: MUTED }}>
                        {row.prevPrice !== null ? displayCurrency(row.prevPrice) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-xs font-bold overflow-hidden" style={{ color: NAVY }}>
                        {displayCurrency(row.currentPrice)}
                      </td>
                      <td className="px-3 py-3 text-right overflow-hidden">
                        {pctStr && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold"
                            style={{
                              backgroundColor: row.direction === "up" ? UP_WEAK : DOWN_WEAK,
                              color: row.direction === "up" ? UP : DOWN,
                            }}
                          >
                            {row.direction === "up" ? "^" : "v"} {pctStr}
                          </span>
                        )}
                      </td>
                      <td className="pr-3 py-3 text-center overflow-hidden">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelected({ invoiceId: row.currentInvoiceId, prevInvoiceId: row.prevInvoiceId, productCode: row.productCode }); }}
                          className="rounded p-1 transition-colors"
                          style={{ color: NAVY, opacity: 0.3 }}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = "0.8")}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = "0.3")}
                          title="Compare invoices"
                        >
                          <Eye size={13} />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ backgroundColor: RAISED, borderBottom: `1px solid ${NAVY}0a` }}>
                        <td colSpan={8} className="px-4 py-3">
                          {invoiceHistory.length === 0 ? (
                            <p className="text-xs" style={{ color: NAVY, opacity: 0.4 }}>No invoice lines found for this item.</p>
                          ) : (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: NAVY, opacity: 0.4 }}>Invoice History ({invoiceHistory.length})</p>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr>
                                    {["Invoice #", "Date", "Qty", "Unit", "Unit Price", "Total", ""].map((h) => (
                                      <th key={h} className="text-left pb-1.5 pr-6 font-semibold" style={{ color: NAVY, opacity: 0.4, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {invoiceHistory.map((line, j) => {
                                    const previousLine = invoiceHistory[j + 1];
                                    return (
                                      <tr key={j} style={{ borderTop: j > 0 ? `1px solid ${NAVY}0a` : undefined }}>
                                        <td className="py-1.5 pr-6 font-semibold tabular-nums" style={{ color: NAVY }}>{line.invoiceNumber ?? "—"}</td>
                                        <td className="py-1.5 pr-6" style={{ color: NAVY, opacity: 0.6 }}>{line.invoiceDate ? new Date(line.invoiceDate).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                                        <td className="py-1.5 pr-6" style={{ color: NAVY }}>{line.quantity ?? "—"}</td>
                                        <td className="py-1.5 pr-6" style={{ color: NAVY, opacity: 0.6 }}>{line.unit ?? "—"}</td>
                                        <td className="py-1.5 pr-6 font-semibold" style={{ color: NAVY }}>
                                          <span className="inline-flex items-center gap-1.5">
                                            <span className="inline-flex w-3 justify-center">
                                              <PriceDirectionIcon current={line.unitPrice} previous={previousLine?.unitPrice} />
                                            </span>
                                            <span>{line.unitPrice != null ? `$${line.unitPrice.toFixed(2)}` : "—"}</span>
                                          </span>
                                        </td>
                                        <td className="py-1.5 pr-6 font-bold" style={{ color: NAVY }}>{line.totalPrice != null ? `$${line.totalPrice.toFixed(2)}` : "—"}</td>
                                        <td className="py-1.5">
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setSelected({ invoiceId: line.invoiceId, prevInvoiceId: null, productCode: row.productCode }); }}
                                            className="rounded-md px-2 py-1 text-[10px] font-bold transition-all"
                                            style={{ backgroundColor: CHIP, color: NAVY }}
                                          >
                                            View
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function fuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase();
  if (!q) return 1;
  if (t.includes(q)) return 1 + q.length / t.length;
  let qi = 0, score = 0, last = -1;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) { score += last === i - 1 ? 2 : 1; last = i; qi++; }
  }
  return qi < q.length ? 0 : score / (q.length * 2);
}

// ── PriceIndexChart ───────────────────────────────────────────────────────────

const SPEND_COLOR = "#a07428";

function formatSignedPercent(value: number, decimals: number) {
  const normalized = Math.abs(value) < 0.0001 ? 0 : value;
  return `${normalized >= 0 ? "+" : ""}${normalized.toFixed(decimals)}%`;
}

function PriceIndexChart({
  invoices,
  isLoading,
}: {
  invoices: Invoice[];
  isLoading: boolean;
}) {
  const [rootDate, setRootDate] = useState("");
  const [granularity, setGranularity] = useState<"week" | "month">("week");

  const data = useMemo(
    () => buildPriceIndexData(invoices, rootDate || undefined, granularity),
    [invoices, rootDate, granularity],
  );

  const isEmpty = data.chartData.length === 0;
  const baselineLabel = data.baselinePeriod ? formatPeriod(data.baselinePeriod, granularity) : "baseline";

  const idxDecimals = useMemo(() => {
    const maxAbs = data.chartData.reduce((m, p) => Math.max(m, Math.abs(Number(p.index) || 0)), 0);
    return maxAbs < 0.1 ? 2 : maxAbs < 1 ? 1 : 0;
  }, [data.chartData]);

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl" style={panelStyle}>
      <div className="flex flex-shrink-0 flex-col gap-2.5 px-5 pb-2.5 pt-4" style={graphPanelHeaderStyle}>
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="text-[15px] font-semibold tracking-[-0.01em]" style={{ color: NAVY }}>Price Index</h2>
            <InfoTooltip text="Weighted price index over time. Index 100 = baseline date; total spend shows invoice spend for each period." />
          </div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            <div
              className="hidden h-7 flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium sm:inline-flex"
              style={{ color: MUTED, border: `1px solid ${LINE}`, backgroundColor: SURFACE }}
              title="Change baseline date"
            >
              <span>Base</span>
              <b className="font-semibold" style={{ color: NAVY }}>{baselineLabel}</b>
              <span className="mx-0.5 h-3.5 w-px" style={{ backgroundColor: LINE }} />
              <input
                type="date"
                value={rootDate}
                onChange={(e) => setRootDate(e.target.value)}
                onClick={(e) => {
                  const input = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
                  input.showPicker?.();
                }}
                className="h-5 w-[136px] min-w-[136px] cursor-pointer rounded-full bg-transparent px-1 text-[11px] font-semibold outline-none"
                style={{ color: NAVY, colorScheme: "light", accentColor: ORANGE }}
                aria-label="Price index baseline date"
              />
            </div>
            {rootDate && (
              <button
                type="button"
                onClick={() => setRootDate("")}
                className="hidden h-7 rounded-full px-2 text-[11px] font-semibold sm:inline-flex sm:items-center"
                style={{ color: MUTED, border: `1px solid ${LINE}`, backgroundColor: SURFACE }}
              >
                Reset
              </button>
            )}
            <div className="flex gap-0.5 rounded-full p-[2px]" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
              {([
                ["week", "Week"],
                ["month", "Month"],
              ] as const).map(([value, label]) => {
                const active = granularity === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setGranularity(value)}
                    className="h-6 rounded-full px-2.5 text-[11px] font-semibold transition-colors"
                    style={active ? { backgroundColor: ACCENT_WEAK, color: ORANGE } : { color: MUTED }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {!isLoading && !isEmpty && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-0 w-4 flex-shrink-0 border-t-2 border-dashed" style={{ borderColor: NAVY }} />
              <span className="truncate text-[11px]" style={{ color: NAVY }}>Price Index</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-0.5 w-4 flex-shrink-0 rounded-full" style={{ backgroundColor: SPEND_COLOR }} />
              <span className="truncate text-[11px]" style={{ color: NAVY }}>Total Spend</span>
              <span className="hidden flex-shrink-0 text-[11px] font-bold tabular-nums sm:inline" style={{ color: NAVY }}>
                {displayCurrency(data.latestTotalSpend)}
              </span>
            </div>
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-20">
          <p className="text-sm" style={{ color: NAVY, opacity: 0.4 }}>Loading…</p>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-1 items-center justify-center px-6 py-20 text-center">
          <p className="text-sm" style={{ color: NAVY, opacity: 0.4 }}>No product-coded line items to track yet</p>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="min-h-[230px] flex-1 px-3 pb-1 pt-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data.chartData}
                margin={{ top: 6, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={`${NAVY}09`} vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: MUTED, fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: string) => formatPeriod(v, granularity)}
                />
                <YAxis
                  yAxisId="dollar"
                  tick={{ fill: MUTED, fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => formatAxisCurrency(v)}
                  width={54}
                />
                <YAxis
                  yAxisId="idx"
                  orientation="right"
                  tick={{ fill: MUTED, fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => formatSignedPercent(v, idxDecimals)}
                  width={idxDecimals > 0 ? 46 : 40}
                />
                <Tooltip
                  cursor={{ stroke: LINE, strokeWidth: 1 }}
                  wrapperStyle={{ outline: "none" }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const idxEntry = payload.find((p: any) => p.dataKey === "index");
                    const spendEntry = payload.find((p: any) => p.dataKey === "totalSpend");
                    const indexPct = Number(idxEntry?.value ?? 0);
                    const totalSpend = Number(spendEntry?.value ?? 0);
                    return (
                      <div className="rounded-lg px-3 py-2 text-xs shadow-lg" style={{ backgroundColor: "#fff", border: `1px solid ${LINE}`, color: NAVY, minWidth: 160 }}>
                        <p className="mb-2 text-[11px] font-bold" style={{ color: NAVY }}>
                          {formatPeriodDetail(String(label), granularity)}
                        </p>
                        <div className="mb-1.5 flex items-center justify-between gap-4">
                          <span className="flex items-center gap-1.5" style={{ color: MUTED }}>
                            <span className="h-0 w-3 border-t-2 border-dashed" style={{ borderColor: NAVY }} />
                            Price Index
                          </span>
                          <b className="tabular-nums" style={{ color: indexPct >= 0 ? UP : DOWN }}>
                            {formatSignedPercent(indexPct, idxDecimals)}
                          </b>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="flex items-center gap-1.5" style={{ color: MUTED }}>
                            <span className="h-0.5 w-3 rounded-full" style={{ backgroundColor: SPEND_COLOR }} />
                            Total Spend
                          </span>
                          <b className="tabular-nums" style={{ color: NAVY }}>
                            {displayCurrency(totalSpend)}
                          </b>
                        </div>
                      </div>
                    );
                  }}
                />
                <Line
                  yAxisId="idx"
                  type="monotone"
                  dataKey="index"
                  stroke={NAVY}
                  strokeWidth={2.5}
                  strokeDasharray="5 3"
                  dot={(props: any) => {
                    const { cx, cy, payload } = props;
                    return <circle key={`idx-${payload?.month}`} cx={cx} cy={cy} r={3} fill={NAVY} strokeWidth={1.5} />;
                  }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                  name="index"
                />
                <Line
                  yAxisId="dollar"
                  type="monotone"
                  dataKey="totalSpend"
                  stroke={SPEND_COLOR}
                  strokeWidth={2}
                  dot={(props: any) => {
                    const { cx, cy, payload } = props;
                    return <circle key={`spend-${payload?.month}`} cx={cx} cy={cy} r={3} fill={SPEND_COLOR} strokeWidth={1.5} />;
                  }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                  name="totalSpend"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

// ── CategoryBreakdown ─────────────────────────────────────────────────────────

function CategoryBreakdown({ invoices }: { invoices: Invoice[] }) {
  const { data, totalAmount } = useMemo(() => {
    const map = new Map<string, number>();
    for (const inv of invoices) {
      for (const item of inv.lineItems) {
        const cat = item.category || "Other";
        map.set(cat, (map.get(cat) ?? 0) + numberValue(item.totalPrice));
      }
    }
    const entries = Array.from(map.entries());
    const totalAmount = entries.reduce((sum, [, amount]) => sum + amount, 0);
    const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
    const groupedEntries =
      sortedEntries.length > 6
        ? [
            ...sortedEntries.slice(0, 5),
            ["Other categories", sortedEntries.slice(5).reduce((sum, [, amount]) => sum + amount, 0)] as [string, number],
          ]
        : sortedEntries;
    const data = groupedEntries
      .map(([category, amount]) => ({
        category,
        amount,
        percent: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
        color: category === "Other categories"
          ? FAINT
          : CATEGORY_COLORS[category] ?? CATEGORY_COLORS["Other"] ?? ORANGE,
      }));
    return { data, totalAmount };
  }, [invoices]);

  if (data.length === 0) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-xl" style={panelStyle}>
        <div className="flex flex-shrink-0 items-center gap-2.5 px-5 pb-2.5 pt-4" style={graphPanelHeaderStyle}>
          <h2 className="text-[15px] font-semibold tracking-[-0.01em]" style={{ color: NAVY }}>Spend by Category</h2>
        </div>
        <div className="flex flex-1 items-center justify-center py-16">
          <p className="text-sm" style={{ color: NAVY, opacity: 0.4 }}>No spend data yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl" style={panelStyle}>
      <div className="flex flex-shrink-0 items-center gap-2.5 px-5 pb-2.5 pt-4" style={graphPanelHeaderStyle}>
        <h2 className="text-[15px] font-semibold tracking-[-0.01em]" style={{ color: NAVY }}>Spend by Category</h2>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 px-5 pb-5 pt-2 md:grid-cols-[minmax(0,1fr)_170px]">
        <div className="relative h-[260px] min-h-0 md:h-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
              <Pie
                data={data}
                dataKey="amount"
                nameKey="category"
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="86%"
                paddingAngle={1.5}
                cornerRadius={4}
                isAnimationActive={false}
              >
                {data.map((item) => (
                  <Cell key={item.category} fill={item.color} stroke={SURFACE} strokeWidth={3} />
                ))}
              </Pie>
              <Tooltip
                cursor={false}
                wrapperStyle={{ outline: "none" }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const item = payload[0]?.payload as typeof data[number] | undefined;
                  if (!item) return null;
                  return (
                    <div className="rounded-lg px-3 py-2 text-xs shadow-lg" style={{ backgroundColor: "#fff", border: `1px solid ${LINE}`, color: NAVY }}>
                      <div className="mb-1 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="font-bold">{item.category}</span>
                      </div>
                      <div className="flex min-w-[150px] items-center justify-between gap-4" style={{ color: MUTED }}>
                        <span>Total spend</span>
                        <b className="tabular-nums" style={{ color: NAVY }}>{displayCurrency(item.amount)}</b>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-4" style={{ color: MUTED }}>
                        <span>Share</span>
                        <b className="tabular-nums" style={{ color: NAVY }}>{item.percent.toFixed(1)}%</b>
                      </div>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-center">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: MUTED }}>Total</p>
              <p className="mt-1 text-[18px] font-extrabold tabular-nums" style={{ color: NAVY }}>{displayCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="flex min-h-0 flex-col justify-center gap-2.5 overflow-y-auto py-1">
          {data.map((item) => (
            <div key={item.category} className="flex min-w-0 items-center gap-2">
              <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <span className="truncate text-[12px] font-semibold" style={{ color: NAVY }}>{item.category}</span>
                  <span className="text-[11px] font-bold tabular-nums" style={{ color: MUTED }}>{item.percent.toFixed(1)}%</span>
                </div>
                <p className="mt-0.5 truncate text-[11px] tabular-nums" style={{ color: MUTED }}>{displayCurrency(item.amount)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── FoodPriceTrackChart ───────────────────────────────────────────────────────

type TimeWindow = "3m" | "6m" | "1y" | "all";
const TIME_WINDOWS: { value: TimeWindow; label: string }[] = [
  { value: "3m", label: "3M" },
  { value: "6m", label: "6M" },
  { value: "1y", label: "1Y" },
  { value: "all", label: "All" },
];

function FoodPriceTrackChart({ invoices, isLoading }: { invoices: Invoice[]; isLoading: boolean }) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("all");
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchOpen) return;
    function h(e: MouseEvent) { if (!searchRef.current?.contains(e.target as Node)) setSearchOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [searchOpen]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const priceData = useMemo(() => buildPriceIndexData(invoices), [invoices]);

  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const s of priceData.series) if (s.category) cats.add(s.category);
    return [...cats].sort();
  }, [priceData.series]);

  const lockedUnit = useMemo(
    () => priceData.series.find((s) => selectedKeys.has(s.key))?.unit ?? null,
    [priceData.series, selectedKeys],
  );

  const suggestions = useMemo(() => {
    let all = lockedUnit
      ? priceData.series.filter((s) => s.unit === lockedUnit)
      : priceData.series;
    if (selectedCategory) all = all.filter((s) => s.category === selectedCategory);
    if (!query.trim()) return all;
    return all
      .map((s) => ({ ...s, score: Math.max(fuzzyScore(query, s.description), fuzzyScore(query, s.productCode)) }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [priceData.series, query, lockedUnit, selectedCategory]);

  const selectedSeries = useMemo(
    () => priceData.series.filter((s) => selectedKeys.has(s.key)),
    [priceData.series, selectedKeys],
  );

  const displayedSeries = useMemo(
    () => selectedCategory ? selectedSeries.filter((s) => s.category === selectedCategory) : selectedSeries,
    [selectedSeries, selectedCategory],
  );

  const chartData = useMemo(() => {
    if (selectedSeries.length === 0) return [];
    const allMonths = new Set<string>();
    for (const s of selectedSeries) for (const m of s.monthPrices.keys()) allMonths.add(m);
    return [...allMonths].sort().map((month) => {
      const point: Record<string, string | number> = { month };
      for (const s of selectedSeries) {
        const price = s.monthPrices.get(month);
        if (price !== undefined) point[s.key] = price;
      }
      return point;
    });
  }, [selectedSeries]);

  const filteredChartData = useMemo(() => {
    if (timeWindow === "all") return chartData;
    const months = { "3m": 3, "6m": 6, "1y": 12 }[timeWindow];
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);
    const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}`;
    return chartData.filter((d) => (d.month as string) >= cutoffStr);
  }, [chartData, timeWindow]);

  const yDomain = useMemo((): [number, number] => {
    if (filteredChartData.length === 0) return [0, 10];
    let min = Infinity, max = -Infinity;
    for (const point of filteredChartData) {
      for (const s of displayedSeries) {
        const v = (point as Record<string, unknown>)[s.key];
        if (typeof v === "number") { min = Math.min(min, v); max = Math.max(max, v); }
      }
    }
    if (!isFinite(min)) return [0, 10];
    const pad = (max - min) * 0.12 || 0.5;
    return [Math.max(0, min - pad), max + pad];
  }, [filteredChartData, displayedSeries]);

  const displayChartData = useMemo(
    () => filteredChartData.map((d) => ({ ...d, month: formatMonth(d.month as string) })),
    [filteredChartData],
  );

  const barListData = useMemo(() => {
    if (filteredChartData.length !== 1) return [];
    const point = filteredChartData[0];
    return displayedSeries
      .map((s) => {
        const price = point[s.key];
        if (typeof price !== "number") return null;
        return { name: s.description, value: price, color: s.color, href: undefined };
      })
      .filter(Boolean)
      .sort((a, b) => b!.value - a!.value) as { name: string; value: number; color: string }[];
  }, [filteredChartData, displayedSeries]);

  const ChartTooltip = useMemo(() => {
    return function TooltipEl({ payload, active, label }: TremorTooltipProps) {
      if (!active || !payload?.length) return null;
      return (
        <div style={{ backgroundColor: "#fff", border: `1px solid ${NAVY}20`, borderRadius: 10, padding: "10px 14px", fontSize: 11, color: NAVY, boxShadow: "0 6px 20px rgba(80,55,30,0.12)", minWidth: 180 }}>
          <p style={{ fontWeight: 700, marginBottom: 6, fontSize: 12 }}>{label}</p>
          {payload.map((item) => {
            const key = String(item.dataKey ?? "");
            const s = displayedSeries.find((s) => s.key === key);
            if (!s || item.value == null) return null;
            const v = Number(item.value);
            const basePrice = s.monthPrices.get(s.baselineMonth);
            const pct = basePrice ? ((v - basePrice) / basePrice) * 100 : null;
            const pctStr = pct != null ? ` (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)` : "";
            const pctColor = pct == null ? NAVY : pct > 0 ? "#EF4444" : "#10B981";
            return (
              <div key={key} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginTop: 4 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: item.color, flexShrink: 0, marginTop: 2 }} />
                <span style={{ color: NAVY, lineHeight: 1.4 }}>
                  <span style={{ display: "block", fontWeight: 500, fontSize: 11, opacity: 0.75, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.description}</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>${v.toFixed(2)}</span>
                  {pct != null && <span style={{ fontWeight: 600, color: pctColor, marginLeft: 4, fontSize: 11 }}>{pctStr}</span>}
                </span>
              </div>
            );
          })}
        </div>
      );
    };
  }, [displayedSeries]);

  const isEmpty = selectedKeys.size === 0;
  const isCategoryEmpty = !isEmpty && displayedSeries.length === 0;

  return (
    <div className="rounded-lg overflow-hidden flex flex-col" style={{ ...panelStyle, height: 420 }}>
      {/* Header */}
      <div className="px-5 pb-2.5 pt-4 flex-shrink-0 flex items-center justify-between gap-2.5" style={graphPanelHeaderStyle}>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <h2 className="text-[15px] font-semibold tracking-[-0.01em]" style={{ color: NAVY }}>Food Price Tracker</h2>
          <InfoTooltip text="Actual unit price (dollar value) per item over time. Add items to compare." />
          {lockedUnit && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${NAVY}0f`, border: `1px solid ${NAVY}18`, color: NAVY }}>
              unit: {lockedUnit}
              <button onClick={() => setSelectedKeys(new Set())} style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          )}
        </div>

        {/* Time window */}
        <div className="flex items-center gap-0.5 flex-1 justify-center">
          {TIME_WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setTimeWindow(w.value)}
              className="h-6 rounded-full px-2 text-[10px] font-semibold"
              style={{
                color: timeWindow === w.value ? ORANGE : MUTED,
                backgroundColor: timeWindow === w.value ? ACCENT_WEAK : "transparent",
                border: `1px solid ${timeWindow === w.value ? ACCENT_LINE : `${NAVY}12`}`,
                cursor: "pointer",
              }}
            >
              {w.label}
            </button>
          ))}
        </div>

        {/* Item search */}
        <div ref={searchRef} className="relative flex-shrink-0">
          <button
            onClick={() => { setSearchOpen((o) => !o); setQuery(""); }}
            className="flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold"
            style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}`, outline: "none" }}
          >
            <span>+ Add item</span>
          </button>
          {searchOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-20 rounded-lg py-1"
              style={{ width: 260, backgroundColor: "#fff", border: `1px solid ${NAVY}18`, boxShadow: "0 6px 20px rgba(80,55,30,0.12)" }}
            >
              <div className="px-2 pb-1 pt-1">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search items…"
                  className="w-full text-xs rounded-md px-2 py-1.5 outline-none"
                  style={{ backgroundColor: `${NAVY}07`, border: `1px solid ${NAVY}12`, color: NAVY }}
                />
              </div>
              <div className="max-h-56 overflow-y-auto">
                {suggestions.length === 0
                  ? <p className="px-3 py-2 text-xs" style={{ color: NAVY, opacity: 0.4 }}>No items found</p>
                  : suggestions.map((s) => {
                      const selected = selectedKeys.has(s.key);
                      return (
                        <button
                          key={s.key}
                          onClick={() => {
                            setSelectedKeys((prev) => {
                              const next = new Set(prev);
                              selected ? next.delete(s.key) : next.add(s.key);
                              return next;
                            });
                          }}
                          className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-gray-50"
                          style={{ border: "none", background: "none", cursor: "pointer" }}
                        >
                          <span
                            className="flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center"
                            style={{ borderColor: selected ? NAVY : `${NAVY}30`, backgroundColor: selected ? NAVY : "transparent" }}
                          >
                            {selected && <span style={{ color: "#fff", fontSize: 9, lineHeight: 1 }}>✓</span>}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="text-xs block truncate" style={{ color: NAVY }}>{s.description}</span>
                            <span className="text-[10px] block truncate" style={{ color: NAVY, opacity: 0.45 }}>{s.supplierName}</span>
                          </span>
                        </button>
                      );
                    })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category filter */}
      {availableCategories.length > 1 && (
        <div className="px-5 pt-2 pb-0 flex-shrink-0 flex flex-wrap gap-1">
          {availableCategories.map((cat) => {
            const active = selectedCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(active ? null : cat)}
                className="text-[10px] rounded-full px-2 py-0.5 font-medium"
                style={{
                  color: active ? "#fff" : NAVY,
                  backgroundColor: active ? NAVY : `${NAVY}08`,
                  border: `1px solid ${active ? NAVY : `${NAVY}20`}`,
                  cursor: "pointer",
                }}
              >
                {cat}
              </button>
            );
          })}
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory(null)}
              className="text-[10px] rounded-full px-2 py-0.5"
              style={{ color: NAVY, opacity: 0.45, background: "none", border: `1px solid ${NAVY}15`, cursor: "pointer" }}
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* Selected item chips */}
      {selectedSeries.length > 0 && (
        <div className="px-5 pt-2 pb-1 flex-shrink-0 flex flex-wrap gap-1.5 items-center">
          {selectedSeries.map((s) => (
            <span
              key={s.key}
              className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5"
              style={{ backgroundColor: `${s.color}18`, border: `1px solid ${s.color}40`, color: s.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
              {s.description}
              <button
                onClick={() => setSelectedKeys((prev) => { const n = new Set(prev); n.delete(s.key); return n; })}
                style={{ background: "none", border: "none", cursor: "pointer", color: s.color, padding: 0, lineHeight: 1, marginLeft: 1 }}
              >×</button>
            </span>
          ))}
          {selectedSeries.length > 1 && (
            <button
              onClick={() => setSelectedKeys(new Set())}
              className="text-[10px] rounded-full px-2 py-0.5"
              style={{ color: NAVY, opacity: 0.5, background: "none", border: `1px solid ${NAVY}20`, cursor: "pointer" }}
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Chart or empty state */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm" style={{ color: NAVY, opacity: 0.4 }}>Loading…</p>
        </div>
      ) : isEmpty ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2">
          <p className="text-sm font-medium" style={{ color: NAVY, opacity: 0.35 }}>No items selected</p>
          <p className="text-xs" style={{ color: NAVY, opacity: 0.25 }}>Click "+ Add item" to start tracking prices</p>
        </div>
      ) : isCategoryEmpty ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2">
          <p className="text-sm font-medium" style={{ color: NAVY, opacity: 0.35 }}>No selected items in this category</p>
          <button
            onClick={() => setSelectedCategory(null)}
            className="text-xs px-3 py-1 rounded-md font-medium"
            style={{ color: "#fff", backgroundColor: NAVY, border: "none", cursor: "pointer" }}
          >
            Clear filter
          </button>
        </div>
      ) : filteredChartData.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2">
          <p className="text-sm font-medium" style={{ color: NAVY, opacity: 0.35 }}>No data in this period</p>
          <button
            onClick={() => setTimeWindow("all")}
            className="text-xs px-3 py-1 rounded-md font-medium"
            style={{ color: "#fff", backgroundColor: NAVY, border: "none", cursor: "pointer" }}
          >
            Show all time
          </button>
        </div>
      ) : filteredChartData.length === 1 ? (
        <div className="flex-1 min-h-0 px-5 py-4 overflow-y-auto">
          <p className="text-[10px] font-semibold uppercase tracking-widest mb-3" style={{ color: NAVY, opacity: 0.4 }}>
            Current price — {displayChartData[0]?.month}
          </p>
          <div className="flex flex-col gap-2.5">
            {barListData.map((item) => {
              const maxVal = Math.max(...barListData.map((d) => d.value));
              const pct = maxVal > 0 ? (item.value / maxVal) * 100 : 0;
              return (
                <div key={item.name} className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="text-xs truncate flex-shrink-0" style={{ color: NAVY, width: 160 }}>{item.name}</span>
                  <div className="flex-1 relative h-5 flex items-center">
                    <div
                      className="absolute left-0 top-0 h-full rounded"
                      style={{ width: `${pct}%`, backgroundColor: `${item.color}28`, border: `1px solid ${item.color}50` }}
                    />
                  </div>
                  <span className="text-xs font-bold flex-shrink-0" style={{ color: NAVY, minWidth: 52, textAlign: "right" }}>
                    ${item.value.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 px-2 pb-3 pt-1">
          <TremorLineChart
            className="h-full"
            data={displayChartData}
            index="month"
            categories={displayedSeries.map((s) => s.key)}
            colors={displayedSeries.map((s) => s.color)}
            valueFormatter={(v: number) => `$${v % 1 === 0 ? v : v.toFixed(2)}`}
            showLegend={false}
            minValue={yDomain[0]}
            maxValue={yDomain[1]}
            yAxisWidth={68}
            connectNulls={false}
            customTooltip={ChartTooltip}
          />
        </div>
      )}
    </div>
  );
}

// ── Period comparison helpers ─────────────────────────────────────────────────

function getPreviousPeriodRange(
  range: DateRange,
  customStart: string,
  customEnd: string,
): { start: string; end: string } | null {
  const now = new Date();
  if (range === "day") {
    const d = new Date(now);
    d.setDate(d.getDate() - 1);
    const s = localIsoDate(d);
    return { start: s, end: s };
  }
  if (range === "week") {
    const end = new Date(now); end.setDate(now.getDate() - 7);
    const start = new Date(now); start.setDate(now.getDate() - 14);
    return { start: localIsoDate(start), end: localIsoDate(end) };
  }
  if (range === "month") {
    const end = new Date(now); end.setDate(now.getDate() - 30);
    const start = new Date(now); start.setDate(now.getDate() - 60);
    return { start: localIsoDate(start), end: localIsoDate(end) };
  }
  if (range === "custom" && customStart && customEnd) {
    const startMs = new Date(customStart).getTime();
    const endMs = new Date(customEnd).getTime();
    const duration = endMs - startMs;
    const prevEnd = new Date(startMs - 86400000);
    const prevStart = new Date(prevEnd.getTime() - duration);
    return { start: localIsoDate(prevStart), end: localIsoDate(prevEnd) };
  }
  return null;
}

function getComparePeriodLabel(range: DateRange): string {
  if (range === "day") return "vs yesterday";
  if (range === "week") return "vs prev week";
  if (range === "month") return "vs prev month";
  if (range === "custom") return "vs prev period";
  return "";
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeBusiness } = useBusiness();
  const userDisplayName = getUserDisplayName(user);
  const [dateRange, setDateRange] = useState<DateRange>("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const dateFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!customPickerOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (dateFilterRef.current?.contains(event.target as Node)) return;
      setCustomPickerOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setCustomPickerOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [customPickerOpen]);

  const { data: summary, error: summaryError } = useQuery<DashboardSummary>({
    queryKey: ["dashboard-summary", activeBusiness?.id],
    queryFn: () => fetchDashboardSummary(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });

  const { data: invoices, error: invoicesError } = useQuery<Invoice[]>({
    queryKey: ["invoices", activeBusiness?.id],
    queryFn: () => fetchProcessedInvoices(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });

  const { data: businessDetails } = useQuery<BusinessDetails>({
    queryKey: ["business", activeBusiness?.id],
    queryFn: () => fetchBusinessDetails(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });
  const categories = getCategoriesForIndustry(businessDetails?.industry);

  const allInvoices: Invoice[] = invoices ?? [];
  const filtered = filterByDate(allInvoices, dateRange, customStart, customEnd);

  const totalSpend = filtered.reduce((s, i) => s + numberValue(i.totalAmount), 0);
  const flaggedCount = filtered.filter((i) => i.alarms.some((a) => !a.resolvedAt && a.level === "critical")).length;

  const s = summary ?? EMPTY_SUMMARY;
  const isFiltered = dateRange !== "all";
  const displayTotal = isFiltered ? filtered.length : s.totalInvoices;
  const displaySpend = isFiltered ? totalSpend : s.totalSpend;
  const displayFlagged = isFiltered ? flaggedCount : s.flaggedCount;

  const dateLabel = DATE_FILTERS.find((d) => d.value === dateRange)?.label ?? "";

  const supplierSummary = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const inv of filtered) map.set(inv.supplier.id, { id: inv.supplier.id, name: inv.supplier.name });
    return Array.from(map.values());
  }, [filtered]);

  const prevRange = getPreviousPeriodRange(dateRange, customStart, customEnd);
  const prevFiltered = prevRange
    ? allInvoices.filter((inv) => {
        const d = inv.invoiceDate;
        return Boolean(d && d >= prevRange.start && d <= prevRange.end);
      })
    : [];
  const prevSpend = prevFiltered.reduce((s, i) => s + numberValue(i.totalAmount), 0);
  const spendChangePct = dateRange !== "all" && prevSpend > 0
    ? ((displaySpend - prevSpend) / prevSpend) * 100
    : null;
  const countChangePct = dateRange !== "all" && prevFiltered.length > 0
    ? ((displayTotal - prevFiltered.length) / prevFiltered.length) * 100
    : null;
  const compareLabel = getComparePeriodLabel(dateRange);
  const statTrends = useMemo(
    () => buildStatTrend(filtered, dateRange, customStart, customEnd),
    [filtered, dateRange, customStart, customEnd],
  );

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: CANVAS }}>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex flex-col gap-[18px]">
          <div className="flex flex-wrap items-end gap-3.5">
            <div className="min-w-0 max-w-[420px]">
              <h1 className="m-0 truncate text-[27px] font-semibold leading-tight tracking-[-0.01em]" style={{ color: NAVY }}>
                G'day, {userDisplayName}
              </h1>
            </div>
            <div className="pb-1 text-[13.5px]" style={{ color: MUTED }}>
              {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>

            <div ref={dateFilterRef} className="relative ml-auto flex items-center gap-2">
              <Calendar size={15} style={{ color: FAINT }} />
              <div className="flex gap-0.5 rounded-full p-[3px]" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
                {DATE_FILTERS.map(({ value, label }) => {
                  const active = dateRange === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setDateRange(value);
                        setCustomPickerOpen(value === "custom");
                      }}
                      className="h-7 rounded-full px-3 text-xs font-semibold transition-colors"
                      style={active
                        ? { backgroundColor: ACCENT_WEAK, color: ORANGE }
                        : { color: MUTED }
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {dateRange === "custom" && customPickerOpen && (
                <div
                  className="absolute right-0 top-full z-30 mt-2 rounded-xl p-4"
                  style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: "0 16px 40px rgba(80,55,30,0.16)" }}
                >
                  <DateRangeCalendar
                    start={customStart}
                    end={customEnd}
                    onChange={({ start, end }) => {
                      setCustomStart(start);
                      setCustomEnd(end);
                    }}
                  />
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setCustomPickerOpen(false)}
                      className="rounded-lg px-4 py-2 text-xs font-bold text-white"
                      style={{ backgroundColor: ORANGE }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>

          {(summaryError || invoicesError) && (
            <div
              className="rounded-lg px-4 py-3 text-sm"
              style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}
            >
              Could not load business invoice data: {((summaryError || invoicesError) as Error).message}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3.5 xl:grid-cols-4">
            <StatCard
              label="Total Spend"
              value={displayCurrency(displaySpend)}
              info="Total invoice spend for the selected period."
              change={spendChangePct !== null ? { pct: spendChangePct, compareLabel, invert: true } : null}
              trend={statTrends.spend}
              chartType="area"
              chartValueFormatter={formatAxisCurrency}
            />
            <StatCard
              label="Invoices Scanned"
              value={String(displayTotal)}
              info="Number of invoices processed in the selected period."
              change={countChangePct !== null ? { pct: countChangePct, compareLabel, invert: false } : null}
              trend={statTrends.count}
              chartType="bar"
            />
            <StatCard
              label="Price Flags"
              value={String(displayFlagged)}
              info="Invoices with unresolved critical price alerts."
              sub={displayFlagged > 0 ? "need attention" : "all clear"}
              onClick={() => navigate("/invoices", { state: { status: "flagged" } })}
            />
            <StatCard
              label="Active Suppliers"
              value={String(supplierSummary.length)}
              info="Distinct suppliers invoiced in the selected period."
              sub={dateLabel}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.55fr_1fr] xl:h-[410px]">
            <div className="h-full min-h-[410px] xl:min-h-0">
              <PriceIndexChart invoices={allInvoices} isLoading={!invoices} />
            </div>
            <div className="h-full min-h-[410px] xl:min-h-0">
              <CategoryBreakdown invoices={filtered} />
            </div>
          </div>

          <PriceChangePanel
            invoices={allInvoices}
            businessId={activeBusiness?.id}
            categories={categories}
          />
        </div>
      </div>
    </div>
  );
}
